import React, { useEffect, useRef, useState } from 'react';
import { CheckRow, Field, SearchSelect, Select } from '../components/controls';
import { SIDE_WORD, SURPRISE, TEXT_DEBOUNCE_MS } from '../../constants';
import { fmtDatePill } from '../state/format';
import { groupOf } from '../state/emptyDoc';
import { useWeddingDay } from '../state/useWeddingDay';

const e = React.createElement;

// A surprise is kept from the partner. Everything else in this section is
// ordinary planning the team and both sides can see.
const isPrivateKind = (kind) => kind.is_private === true || kind.key === SURPRISE;

// Each draft carries its own id, so its row keeps its place and its typing
// while the rows around it are saved and leave the list.
let draftSeq = 0;
const blank = () => {
  draftSeq += 1;
  return { id: `d${draftSeq}`, description: '', event_id: '' };
};

/** One note's own text. Debounced like every other free text field. */
const NoteText = ({ value, label, disabled, onCommit }) => {
  const [text, setText] = useState(value || '');
  const timer = useRef(null);

  useEffect(() => {
    setText(value || '');
  }, [value]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return e(Field, {
    label: 'Description',
    value: text,
    multiline: true,
    disabled,
    placeholder: 'What is planned?',
    'aria-label': label,
    onChange: (next) => {
      setText(next);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => onCommit(next), TEXT_DEBOUNCE_MS);
    },
    onBlur: () => {
      clearTimeout(timer.current);
      if ((value || '') !== text) onCommit(text);
    },
  });
};

/**
 * Day Notes: anything happening during the celebrations that the team should
 * arrive ready for. Only the surprises are kept from the partner; the rest is
 * ordinary planning both sides read.
 *
 * Each note is a moment on one of the booking's own events, so the rows are
 * read off the events rather than out of the section's document. A row is
 * written only once it says what is planned and which event it belongs to:
 * a half filled row has nothing to save yet, so it is held on screen.
 */
const NotesSection = ({ section }) => {
  const {
    doc, events, locked, sharedLocked, readOnly, canEdit, fieldErrors,
    setText, setValue, addActivity, editActivity, removeActivity,
  } = useWeddingDay();
  // Ops reading the couple's form, or anyone who cannot write it. Their
  // writes are refused anyway, so offering them a row to type in only
  // promises something that will not be kept.
  const viewOnly = readOnly || canEdit === false;
  const group = groupOf(doc, section.key);
  const groups = section.activity_kinds || [];
  const fields = section.fields || [];
  const none = !!group.nothing_planned;
  // The rows not saved yet, per group, held here because the server has
  // nothing to hold one against until it says what and when.
  const [drafts, setDrafts] = useState({});
  // The one empty row an untouched group offers. Kept rather than made
  // fresh each render, or its id, and the text typed into it, would not
  // survive the next one.
  const spare = useRef({});

  // The booking's own events, rituals the couple added included. Never free
  // text: a note belongs to something on the day.
  const eventOptions = (events || []).map((x) => ({
    value: String(x.id),
    label: `${x.name} · ${fmtDatePill(x.date)}`,
  }));

  // Every moment on the day, flattened out of the events that carry them.
  // The server has already dropped the partner's surprises.
  const moments = (events || []).reduce(
    (all, entry) => all.concat((entry.activities || []).map((row) => ({ ...row, entry }))),
    []
  );

  // Anything actually written down. The partner's surprises are already out
  // of this list, so a side is never held to a card it cannot see.
  const planned = moments.length > 0;

  // A surprise stays this side's own until they submit; the rest closes with
  // the schedule, the way every other shared answer does.
  const shutFor = (kind) => viewOnly
    || (isPrivateKind(kind) ? locked : locked || sharedLocked);

  // A group with nothing in it offers one row to write in. Once it holds
  // something, the adder is how the next one is started: saving a note used
  // to leave a fresh empty row behind it every time.
  const draftsOf = (key) => {
    const list = drafts[key];
    if (Array.isArray(list) && list.length) return list;
    if (moments.some((row) => row.type === key)) return [];
    if (!spare.current[key]) spare.current[key] = blank();
    return [spare.current[key]];
  };

  const writeDrafts = (key, rows) => setDrafts((all) => ({ ...all, [key]: rows }));

  // One draft changed by id, against the list as it is when the update runs:
  // a save answers after the couple may have added or written other rows.
  const changeDraft = (key, fn) => setDrafts((all) => {
    const list = Array.isArray(all[key]) && all[key].length
      ? all[key]
      : (spare.current[key] ? [spare.current[key]] : []);
    return { ...all, [key]: fn(list) };
  });

  // Saved the moment it says both what is planned and which event it is on.
  // Until then it is only a row on screen, because half a moment is not one.
  // It stays on screen until the save lands: a refused one keeps what they
  // typed, with the reason under it.
  const commitDraft = (kind, id, next) => {
    if (!next.description.trim() || !next.event_id) {
      changeDraft(kind.key, (list) => list.map((row) => (row.id === id ? next : row)));
      return;
    }
    changeDraft(kind.key, (list) =>
      list.map((row) => (row.id === id ? { ...next, saving: true } : row)));
    addActivity(next.event_id, {
      type: kind.key,
      description: next.description.trim(),
    }, id).then((saved) => {
      if (saved && spare.current[kind.key] && spare.current[kind.key].id === id) {
        delete spare.current[kind.key];
      }
      changeDraft(kind.key, (list) => (saved
        ? list.filter((row) => row.id !== id)
        : list.map((row) => (row.id === id ? { ...row, saving: false } : row))));
    });
  };

  // One saved moment. The partner's rows are read only: a shared moment is
  // read by both of them but stays its author's to change.
  const savedRow = (kind, row) =>
    e('div', { className: 'wd-item', key: String(row.id) }, [
      e(NoteText, {
        key: 'desc',
        label: `${kind.label} description`,
        value: row.description,
        disabled: shutFor(kind) || !row.is_mine,
        onCommit: (value) => {
          if (!value.trim()) {
            removeActivity(row.entry.id, row.id);
            return;
          }
          editActivity(row.entry.id, row.id, { description: value.trim() });
        },
      }),
      e('div', { className: 'wd-noterow', key: 'row' }, [
        e(SearchSelect, {
          key: 'event',
          label: 'Which event does this apply to?',
          value: String(row.entry.id),
          options: eventOptions,
          placeholder: 'Type to search, for example haldi',
          disabled: shutFor(kind) || !row.is_mine,
          onChange: (value) => {
            if (!value || String(value) === String(row.entry.id)) return;
            editActivity(row.entry.id, row.id, { entry_id: value });
          },
        }),
        // Said on the partner's rows only, so nobody wonders why the fields
        // will not take what they type.
        row.is_mine
          ? null
          : e(
              'p',
              { className: 'wd-caption', key: 'whose' },
              `Added by ${SIDE_WORD[row.side] || 'your partner'}.`
            ),
        shutFor(kind) || !row.is_mine
          ? null
          : e(
              'button',
              {
                key: 'rm',
                type: 'button',
                className: 'wd-rm',
                onClick: () => removeActivity(row.entry.id, row.id),
              },
              // Named, because four groups of notes each carry their own.
              `Remove ${String(kind.label || 'note').toLowerCase()}`
            ),
      ]),
    ]);

  // A row being written. It saves itself the moment both halves are there.
  const draftRow = (kind, draft) =>
    e('div', { className: 'wd-item', key: `draft-${draft.id}` }, [
      e(NoteText, {
        key: 'desc',
        label: `New ${String(kind.label || 'note').toLowerCase()} description`,
        value: draft.description,
        disabled: shutFor(kind) || !!draft.saving,
        onCommit: (value) => commitDraft(kind, draft.id, { ...draft, description: value }),
      }),
      e('div', { className: 'wd-noterow', key: 'row' }, [
        e(SearchSelect, {
          key: 'event',
          label: 'Which event does this apply to?',
          value: draft.event_id,
          options: eventOptions,
          placeholder: 'Type to search, for example haldi',
          disabled: shutFor(kind) || !!draft.saving,
          // A note with nothing to hang on keeps the section open, so say what
          // is missing rather than letting it read as answered.
          caption:
            draft.description.trim() && !draft.event_id
              ? 'Pick the event this belongs to.'
              : undefined,
          onChange: (value) => commitDraft(kind, draft.id, { ...draft, event_id: value }),
        }),
      ]),
    ]);

  return e('div', null, [
    section.question ? e('p', { className: 'wd-intro', key: 'intro' }, section.question) : null,
    e(
      'div',
      { key: 'groups' },
      groups.map((kind) => {
        const rows = moments.filter((row) => row.type === kind.key);
        const pending = draftsOf(kind.key);
        // Read off every event this group touches, saved rows and drafts
        // alike, since a save is answered against the event it went to.
        const error = rows.map((row) => row.entry.id)
          .concat(pending.map((d) => d.event_id))
          .filter(Boolean)
          .map((entryId) => fieldErrors[`activity:${entryId}`])
          .find(Boolean);
        return e('div', { className: 'wd-group', key: kind.key }, [
          e('div', { className: 'wd-card', key: 'card' }, [
            e('h3', { className: 'wd-group-head', key: 'h' }, kind.label),
            kind.examples
              ? e('p', { className: 'wd-examples', key: 'x' }, `Examples: ${kind.examples}`)
              : null,
            e(
              'p',
              { className: 'wd-note is-strong', key: 'who' },
              isPrivateKind(kind)
                ? 'Only you and our team can see this. Your partner won’t.'
                : 'Shared with your partner and our team.'
            ),
            e(
              'div',
              { className: 'wd-items', key: 'items' },
              rows.map((row) => savedRow(kind, row)).concat(
                shutFor(kind) ? [] : pending.map((d) => draftRow(kind, d))
              )
            ),
            // A closed group with nothing in it would otherwise be a
            // heading with empty space under it, which reads as something
            // that failed to load rather than as an answer of "none".
            shutFor(kind) && !rows.length
              ? e('p', { className: 'wd-caption', key: 'none' },
                  'Nothing planned in this group.')
              : null,
            error ? e('p', { className: 'wd-fielderror', key: 'err' }, error) : null,
          ]),
          // The adder sits under the card rather than inside it: it adds a
          // note, it is not part of the ones already written.
          shutFor(kind)
            ? null
            : e(
                'button',
                {
                  key: 'add',
                  type: 'button',
                  className: 'wd-adder',
                  onClick: () => writeDrafts(kind.key, pending.concat(blank())),
                },
                '+ Add another'
              ),
        ]);
      })
    ),
    e('div', { className: 'wd-card', key: 'none', style: { marginTop: 22 } }, [
      e('span', { className: 'wd-flabel', key: 'l' }, 'If nothing is planned'),
      e(CheckRow, {
        key: 'row',
        checked: none && !planned,
        // Saying nothing is planned while something is written down says two
        // things at once, so the moment a card is saved the question stops
        // being one the couple can answer.
        disabled: viewOnly || locked || sharedLocked || planned,
        label: 'Nothing special planned',
        onToggle: () => setValue(section.key, 'nothing_planned', !none),
      }),
      planned
        ? e('p', { className: 'wd-caption', key: 'why' },
          'You have written something below, so this no longer applies.')
        : null,
    ]),
    // Whatever else the payload puts here, such as a backup contact.
    fields.length
      ? e('div', { className: 'wd-card', key: 'fields', style: { marginTop: 22 } }, [
          e('div', { className: 'wd-grid2', key: 'grid' }, fields.map((field) =>
            field.kind === 'choice' && (field.options || []).length
              ? e(Select, {
                  key: field.key,
                  label: field.label,
                  value: group[field.key],
                  caption: field.caption,
                  disabled: viewOnly || locked || sharedLocked,
                  options: [{ value: '', label: field.placeholder || 'Select one' }].concat(
                    field.options.map((o) => ({ value: String(o.id), label: o.label }))
                  ),
                  onChange: (v) => setValue(section.key, field.key, v),
                })
              : e(Field, {
                  key: field.key,
                  label: field.label,
                  value: group[field.key],
                  type: field.input_type || 'text',
                  multiline: field.kind === 'long_text',
                  placeholder: field.placeholder,
                  caption: field.caption,
                  disabled: viewOnly || locked || sharedLocked,
                  onChange: (v) => setText(section.key, field.key, v),
                })
          )),
        ])
      : null,
  ]);
};

export default NotesSection;
